import { RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { useLive } from '@/lib/use-live';
import { plural } from './takeoff-preview-model';
import { motivoSemQuantitativo, type QuantitativoDoCronograma } from './use-takeoff-lines';

export interface TakeoffDriftBannerProps {
  /** O quantitativo da obra, lido pela página (`useTakeoffLines`). */
  readonly takeoff: QuantitativoDoCronograma;
  /** Quem pode aplicar (`schedule.import`) vê "Ver e aplicar"; os outros, só as diferenças. */
  readonly canApply: boolean;
  readonly onOpen: () => void;
}

/**
 * A faixa "o quantitativo mudou em N itens" (Fase 31, RN-419, D3).
 *
 * O item gerado guarda o número que o quantitativo tinha; quando o de agora difere, a faixa
 * avisa quantos itens mudaram, entraram ou saíram, e leva às diferenças. Sem item gerado
 * (`generated` falso) ou sem diferença, ela não aparece.
 *
 * Quando o quantitativo não pôde ser lido, ela diz isso — e não "nada mudou".
 */
export function TakeoffDriftBanner({ takeoff, canApply, onOpen }: TakeoffDriftBannerProps) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const linhas = takeoff.estado === 'PRONTO' ? takeoff.linhas : undefined;

  const drift = useLive(
    () =>
      linhas === undefined || session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.getScheduleTakeoffDrift.execute(
            { projectId: activeProjectId, takeoff: [...linhas] },
            session,
          ),
    [linhas, session?.userId, activeProjectId],
  );

  const semQuantitativo = motivoSemQuantitativo(takeoff);
  if (semQuantitativo !== undefined || (drift !== undefined && !drift.ok)) {
    return (
      <p role="status" className="text-sm text-(--color-content-muted)" data-faixa-do-quantitativo>
        Não deu para conferir se o quantitativo mudou.{' '}
        {semQuantitativo ?? (drift !== undefined && !drift.ok ? drift.error.message : '')}
      </p>
    );
  }
  if (linhas === undefined || drift === undefined || !drift.value.generated) return null;

  const { changed, added, gone } = drift.value;
  const total = changed + added + gone;
  if (total === 0) return null;

  const partes = [
    changed > 0 ? plural(changed, 'com número novo', 'com número novo') : undefined,
    added > 0 ? plural(added, 'novo no quantitativo', 'novos no quantitativo') : undefined,
    gone > 0 ? plural(gone, 'saiu do quantitativo', 'saíram do quantitativo') : undefined,
  ].filter((parte): parte is string => parte !== undefined);

  return (
    <div
      role="status"
      data-faixa-do-quantitativo
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-(--color-state-info) bg-(--color-state-info)/10 px-4 py-3 text-sm print:hidden"
    >
      <RefreshCcw className="size-4 shrink-0 text-(--color-state-info)" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">O quantitativo mudou em {plural(total, 'item', 'itens')}</p>
        <p className="text-xs text-(--color-content-muted)">
          {partes.join(' · ')}. As datas e o avanço não mudam.
        </p>
      </div>
      <Button type="button" variant="secondary" size="sm" onClick={onOpen}>
        {canApply ? 'Ver e aplicar' : 'Ver as diferenças'}
      </Button>
    </div>
  );
}
